import { type ReactElement } from 'react';

import { BitbucketIntegration } from '@/components/integrations/BitbucketIntegration';
import { GithubIntegration } from '@/components/integrations/GithubIntegration';
import { IntegrationSection } from '@/components/integrations/IntegrationSection';
import { SamlSsoIntegration } from '@/components/integrations/SamlSsoIntegration';
import { ScimIntegration } from '@/components/integrations/ScimIntegration';
import { SiemIntegration } from '@/components/integrations/SiemIntegration';
import { SlackIntegration } from '@/components/integrations/SlackIntegration';
import {
  isRenderable,
  useBitbucketIntegrationAccess,
  useGithubIntegrationAccess,
  useSamlSsoIntegrationAccess,
  useScimProvisioningIntegrationAccess,
  useSiemIntegrationAccess,
  useSlackIntegrationAccess,
} from '@/hooks/useIntegrationAccess';

export function OrganizationIntegrationsPanel(): ReactElement {
  const github = useGithubIntegrationAccess();
  const bitbucket = useBitbucketIntegrationAccess();
  const samlSso = useSamlSsoIntegrationAccess();
  const scim = useScimProvisioningIntegrationAccess();
  const siem = useSiemIntegrationAccess();
  const slack = useSlackIntegrationAccess();

  const hasSourceControl = isRenderable(github) || isRenderable(bitbucket);
  const hasIdentityAndAccess =
    isRenderable(samlSso) || isRenderable(scim) || isRenderable(siem);

  return (
    <div data-testid="integrations-sections" className="flex flex-col gap-7">
      {hasSourceControl && (
        <IntegrationSection
          title="Source Control"
          subtitle="Connect your workspace to invite organization members to Kirin"
        >
          {isRenderable(github) && <GithubIntegration access={github} />}
          {isRenderable(bitbucket) && (
            <BitbucketIntegration access={bitbucket} />
          )}
        </IntegrationSection>
      )}
      {hasIdentityAndAccess && (
        <IntegrationSection
          title="Identity & Access"
          subtitle="Manage authentication, access, and security log forwarding"
        >
          {isRenderable(samlSso) && <SamlSsoIntegration access={samlSso} />}
          {isRenderable(scim) && <ScimIntegration access={scim} />}
          {isRenderable(siem) && <SiemIntegration access={siem} />}
        </IntegrationSection>
      )}
      {isRenderable(slack) && (
        <IntegrationSection
          title="Notifications"
          subtitle="Configure Slack notifications for your organization's scan alerts"
        >
          <SlackIntegration access={slack} />
        </IntegrationSection>
      )}
    </div>
  );
}

OrganizationIntegrationsPanel.displayName = 'OrganizationIntegrationsPanel';
